import type { QueryClient } from "@tanstack/react-query";
import { ClassRoomsApi, LocationsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, createOpenApiConfiguration } from "@/lib/utils";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";

const DEFAULT_TENANT_ID = resolveEffectiveTenantNumber();

const CLASS_ROOMS_PUBLIC_PAGE = 1;
const CLASS_ROOMS_PUBLIC_PAGE_SIZE = 1000;
const CLASS_ROOMS_PUBLIC_ACTIVE = true;

export async function prefetchLocationPublic(
  queryClient: QueryClient,
  locationId: number,
  accessToken?: string,
) {
  const service = accessToken
    ? createAuthenticatedApi(LocationsApi, accessToken)
    : new LocationsApi(createOpenApiConfiguration());

  await queryClient.prefetchQuery({
    queryKey: [QueryKeys.Locations, locationId, "public"],
    queryFn: () =>
      service.getLocationById({ id: locationId, xTenantID: DEFAULT_TENANT_ID }),
  });

  /** Class-rooms list filtered by location on the client - same query as `useClassRoomsGetPublic`. */
  const classRoomsService = accessToken
    ? createAuthenticatedApi(ClassRoomsApi, accessToken)
    : new ClassRoomsApi(createOpenApiConfiguration());

  await queryClient.prefetchQuery({
    queryKey: [
      QueryKeys.ClassRooms,
      DEFAULT_TENANT_ID,
      "public",
      CLASS_ROOMS_PUBLIC_PAGE,
      CLASS_ROOMS_PUBLIC_PAGE_SIZE,
      CLASS_ROOMS_PUBLIC_ACTIVE,
    ],
    queryFn: () =>
      classRoomsService.apiClassRoomsScrollGet({
        page: CLASS_ROOMS_PUBLIC_PAGE,
        pageSize: CLASS_ROOMS_PUBLIC_PAGE_SIZE,
        active: CLASS_ROOMS_PUBLIC_ACTIVE,
        xTenantID: DEFAULT_TENANT_ID,
      }),
    staleTime: 5 * 60 * 1000,
  });

  return queryClient;
}
